import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, TriangleAlert, UserX } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button, EmptyState, Field, Input, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';
import type { AccountDeletionPreview, BlockedUser } from '@/lib/types';

/** The word that must be typed to confirm. The server checks it too; this is only the keyboard. */
const CONFIRMATION_WORD = 'OBRIŠI';

export function SettingsPage() {
  useSeo({ title: `Podešavanja | ${SITE_NAME}`, noIndex: true });

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="font-display text-2xl font-bold text-body">Podešavanja</h1>

      <div className="mt-8 space-y-10">
        <BlockedUsers />
        <DeleteAccount />
      </div>
    </div>
  );
}

/**
 * The list of people this user has blocked, and the way back.
 *
 * A block that cannot be undone is a trap rather than a tool — somebody who blocks a dealer in
 * frustration and then wants the car next week should not have to write to support about it.
 */
function BlockedUsers() {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['blocked-users'],
    queryFn: () => api<BlockedUser[]>('/api/blocks'),
  });

  const unblock = useMutation({
    mutationFn: (id: string) => api(`/api/blocks/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blocked-users'] }),
  });

  return (
    <section>
      <h2 className="text-lg font-semibold text-body">Blokirani korisnici</h2>
      <p className="mt-1 text-sm text-muted">
        Sa blokiranim korisnikom ne možete razmenjivati poruke, ni u jednom smeru. Blokirani korisnik
        o tome ne dobija obaveštenje.
      </p>

      <div className="mt-4">
        {isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : data && data.length > 0 ? (
          <ul className="space-y-2">
            {data.map((person) => (
              <li
                key={person.id}
                className="flex items-center gap-3 rounded-xl border border-default bg-raised p-3"
              >
                {person.profilePhotoUrl ? (
                  <img
                    src={person.profilePhotoUrl}
                    alt=""
                    className="size-9 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent">
                    {person.displayName.slice(0, 2).toUpperCase()}
                  </span>
                )}

                <span className="min-w-0 flex-1 truncate text-sm font-medium text-body">
                  {person.displayName}
                </span>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => unblock.mutate(person.id)}
                  disabled={unblock.isPending}
                >
                  Odblokiraj
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<ShieldOff size={32} />}
            title="Nikoga niste blokirali"
            description="Korisnika možete blokirati iz same prepiske."
          />
        )}
      </div>
    </section>
  );
}

/**
 * Deleting the account.
 *
 * The counts come from the server rather than being described in general terms, because "your
 * listings will be deleted" and "your 6 listings and 41 photographs will be deleted" are read very
 * differently by the person about to press the button.
 */
function DeleteAccount() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: preview } = useQuery({
    queryKey: ['deletion-preview'],
    queryFn: () => api<AccountDeletionPreview>('/api/account/deletion-preview'),
    enabled: open,
  });

  const remove = useMutation({
    mutationFn: () =>
      api('/api/account/delete', {
        method: 'POST',
        body: { password: password || null, confirmation },
      }),
    onSuccess: async () => {
      // The account is gone; the session in this tab is the only thing left pointing at it.
      await logout();
      navigate('/', { replace: true });
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiError ? cause.message : 'Brisanje nije uspelo. Pokušajte ponovo.',
      ),
  });

  const confirmed = confirmation.trim().toUpperCase() === CONFIRMATION_WORD;

  return (
    <section className="rounded-[var(--radius-panel)] border border-danger/25 bg-danger/5 p-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-danger">
        <TriangleAlert size={18} />
        Brisanje naloga
      </h2>

      <p className="mt-2 text-sm text-muted">
        Brisanje je trajno. Nalog, oglasi, fotografije i prepiske nestaju i ne mogu se vratiti.
        Detalje pročitajte na stranici{' '}
        <Link to="/brisanje-naloga" className="text-accent hover:underline">
          brisanje naloga
        </Link>
        .
      </p>

      {!open ? (
        <Button variant="danger" className="mt-4" onClick={() => setOpen(true)}>
          <UserX size={15} />
          Obriši nalog
        </Button>
      ) : (
        <div className="mt-4 space-y-4">
          {preview && (
            <ul className="space-y-1 rounded-xl border border-default bg-raised p-3 text-sm text-body">
              <Count label="oglasa" value={preview.listings} />
              <Count label="fotografija" value={preview.photos} />
              <Count label="prepiski" value={preview.conversations} />
              <Count label="ocena koje ste napisali" value={preview.reviewsWritten} />
              <Count label="ocena koje ste dobili" value={preview.reviewsReceived} />
            </ul>
          )}

          <Field
            label="Lozinka"
            hint="Ostavite prazno ako se prijavljujete preko Google ili Facebook naloga."
          >
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field label={`Upišite ${CONFIRMATION_WORD} da biste potvrdili`} required>
            <Input
              value={confirmation}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setOpen(false);
                setPassword('');
                setConfirmation('');
                setError(null);
              }}
            >
              Odustani
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              disabled={!confirmed || remove.isPending}
              onClick={() => {
                setError(null);
                remove.mutate();
              }}
            >
              {remove.isPending ? 'Brisanje…' : 'Obriši zauvek'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-baseline justify-between gap-4">
      <span className="text-muted">{label}</span>
      <span className="font-display font-bold tabular-nums">{value}</span>
    </li>
  );
}
